import { useEffect, useState } from 'react';
import { cx } from '../components/ui';

export interface AddressBarProps {
  url: string;
  enabled: boolean;
  onNavigate: (url: string) => void;
  onBack: () => void;
  onForward: () => void;
  onReload: () => void;
}

function IconButton({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="rounded-md p-1.5 text-ink hover:bg-black/5 disabled:text-ink-muted/50 disabled:hover:bg-transparent"
    >
      {children}
    </button>
  );
}

/** Operator-only URL bar. Customers never see this. */
export function AddressBar({
  url,
  enabled,
  onNavigate,
  onBack,
  onForward,
  onReload,
}: AddressBarProps) {
  const [value, setValue] = useState(url);
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    if (!dirty) setValue(url === 'about:blank' ? '' : url);
  }, [url, dirty]);

  return (
    <form
      className="flex items-center gap-1 border-b border-line bg-surface px-2 py-1"
      onSubmit={(e) => {
        e.preventDefault();
        const v = value.trim();
        if (!v || !enabled) return;
        onNavigate(v);
        setDirty(false);
      }}
    >
      <IconButton label="뒤로" onClick={onBack} disabled={!enabled}>
        <svg viewBox="0 0 20 20" className="size-4" fill="currentColor" aria-hidden>
          <path d="M12.7 4.3a1 1 0 0 1 0 1.4L8.4 10l4.3 4.3a1 1 0 0 1-1.4 1.4l-5-5a1 1 0 0 1 0-1.4l5-5a1 1 0 0 1 1.4 0z" />
        </svg>
      </IconButton>
      <IconButton label="앞으로" onClick={onForward} disabled={!enabled}>
        <svg viewBox="0 0 20 20" className="size-4" fill="currentColor" aria-hidden>
          <path d="M7.3 4.3a1 1 0 0 0 0 1.4L11.6 10l-4.3 4.3a1 1 0 1 0 1.4 1.4l5-5a1 1 0 0 0 0-1.4l-5-5a1 1 0 0 0-1.4 0z" />
        </svg>
      </IconButton>
      <IconButton label="새로고침" onClick={onReload} disabled={!enabled}>
        <svg viewBox="0 0 20 20" className="size-4" fill="currentColor" aria-hidden>
          <path d="M10 4a6 6 0 0 0-5.7 4.1 1 1 0 1 1-1.9-.6A8 8 0 0 1 16.4 5.2L18 3.6V9h-5.4l1.9-1.9A6 6 0 0 0 10 4zm6.6 7.9a1 1 0 0 1 1 1.3A8 8 0 0 1 3.6 14.8L2 16.4V11h5.4l-1.9 1.9A6 6 0 0 0 15.7 12.5a1 1 0 0 1 .9-.6z" />
        </svg>
      </IconButton>
      <input
        type="text"
        inputMode="url"
        value={value}
        placeholder={
          enabled
            ? '주소 입력 후 Enter (http/https만 가능)'
            : '제어권이 있어야 주소를 바꿀 수 있습니다'
        }
        disabled={!enabled}
        onChange={(e) => {
          setValue(e.target.value);
          setDirty(true);
        }}
        onBlur={() => setDirty(false)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            setDirty(false);
            setValue(url === 'about:blank' ? '' : url);
            e.currentTarget.blur();
          }
        }}
        className={cx(
          'h-8 flex-1 rounded-md border border-line bg-canvas px-3 font-mono text-xs text-ink focus:border-primary focus:bg-surface focus:outline-none',
          !enabled && 'text-ink-muted',
        )}
      />
    </form>
  );
}
